import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { toast } from "react-hot-toast";
import {
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DownloadIcon,
    PenIcon,
} from "../../../Icons";
import EvaluationSignatureModal from "./EvaluationSignatureModal";
import Button from "../../../components/UI/Button";
import { canUserSign } from "../../../utils/formPermissions";
import InputDatePicker from "./InputDatePicker";
import { EVALUATION_BTN } from "../evaluationTheme";
import { FORM_FIELD } from "../../../ui/formTheme";
import { useSubmitLock } from "../../../hooks/useSubmitLock";

const EvaluationFormFooter = ({
    page,
    setPage,
    totalPages = 1,
    methods,
    onSave,
    onSubmit, // Used for completion in this context
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName,
    adminSignatureDateField = "reviewedBySignedAt",
    reviewedBySignedBy,
    reviewedBySignature,
    isReadOnly = false,
    onNext,
}) => {
    const formContext = methods;
    const { watch } = formContext || {};
    const { isSubmitting, pendingAction, runLocked } = useSubmitLock();
    const actionsDisabled = isReadOnly || isSubmitting;

    // Watch fields directly for robustness, with prop fallbacks
    const watchedSignature = watch ? watch("reviewerSignature") : null;
    const watchedName = watch ? watch("reviewerName") : null;

    const effectiveSignature = watchedSignature || reviewedBySignature;
    const effectiveAdminName = watchedName || reviewedBySignedBy;

    const isLastPage = page === totalPages;
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        try {
            const userStr = localStorage.getItem("currentUser");
            if (userStr) {
                setCurrentUser(JSON.parse(userStr));
            }
        } catch (error) {
            console.error("Error parsing currentUser", error);
        }
    }, []);

    const canSignAdmin = canUserSign(currentUser, 'admin');
    const isAdmin = canSignAdmin;

    const handleNext = async () => {
        if (onNext) {
            await onNext();
        } else {
            setPage((p) => Math.min(totalPages, p + 1));
        }
    };

    const handleAction = async (type) => {
        if (actionsDisabled) return;
        await runLocked(type, async () => {
            await methods.handleSubmit(async (data) => {
                if (type === 'completed' && isAdmin && !effectiveSignature) {
                    toast.error("Reviewer Signature is required to complete.");
                    return;
                }

                if (type === 'save') {
                    if (onSave) await onSave(data, 'save');
                } else if (type === 'completed') {
                    if (onSubmit) await onSubmit(data, 'completed');
                }
            }, () => {
                toast.error("Please fix errors before proceeding.");
            })();
        });
    };

    const adminName = effectiveAdminName || (isAdmin ? (signerName || "Reviewer") : "Pending...");

    return (
        <div className="mt-12">
            {isLastPage && (
                <div className="grid grid-cols-1 gap-8 mb-8 border-t border-indigo-100 pt-8">
                    {/* Admin Signature Section */}
                    {(isAdmin || reviewedBySignature) && (
                        <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-indigo-200 shadow-sm max-w-2xl">
                            <h3 className="text-base font-semibold text-slate-900 mb-6 pb-3 border-b border-indigo-100 uppercase tracking-wide">Reviewer Section</h3>
                            <div className="space-y-5">
                                <div>
                                    <p className="block text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">
                                        Reviewer Full Name
                                    </p>
                                    <div className={FORM_FIELD.readonlyBox}>
                                        {adminName}
                                    </div>
                                </div>

                                <div>
                                    <p className="block text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">
                                        Reviewer Signature
                                    </p>
                                    {effectiveSignature ? (
                                        <div className={FORM_FIELD.readonlyBox}>
                                            <p className="font-script">{effectiveSignature}</p>
                                        </div>
                                    ) : (
                                        canSignAdmin ? (
                                            <button
                                                type="button"
                                                onClick={() => openSignatureModal("admin")}
                                                disabled={actionsDisabled}
                                                className="w-full group px-4 py-3 bg-white text-indigo-700 rounded-lg border-2 border-dashed border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <PenIcon className="h-5 w-5 mr-3 inline text-indigo-500" />
                                                <span className="font-bold text-sm tracking-wide">CLICK TO SIGN ELECTRONICALLY</span>
                                            </button>
                                        ) : (
                                            <div className={`${FORM_FIELD.readonlyBox} min-h-[50px] text-slate-400 italic flex items-center font-normal`}>
                                                Pending Signature
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="mt-auto">
                                    <InputDatePicker
                                        name={adminSignatureDateField}
                                        label="Date"
                                        formatSeparator="/"
                                        disabled={isReadOnly || !canSignAdmin || isSubmitting}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Navigation & Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-indigo-100">
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`${EVALUATION_BTN.outline} px-5`}
                >
                    <ChevronLeftIcon className="h-5 w-5 inline mr-1" />
                    Previous
                </button>

                <div className="flex items-center">
                    <span className="text-sm font-semibold text-indigo-700 bg-indigo-50 px-4 py-2 rounded-full border border-indigo-200 tabular-nums">
                        {page} of {totalPages}
                    </span>
                </div>

                {isLastPage ? (
                    <div className="flex gap-4">
                        {!isReadOnly && (
                            <>
                                <Button
                                    text={pendingAction === "save" && isSubmitting ? "Saving…" : "Save"}
                                    onClick={() => handleAction("save")}
                                    icon={<DownloadIcon className="h-5 w-5" />}
                                    disabled={actionsDisabled}
                                    loading={pendingAction === "save" && isSubmitting}
                                />
                                {isAdmin && (
                                    <Button
                                        text={pendingAction === "completed" && isSubmitting ? "Completing…" : "Completed"}
                                        onClick={() => handleAction("completed")}
                                        variant="success"
                                        icon={<CheckIcon className="h-5 w-5" />}
                                        disabled={actionsDisabled}
                                        loading={pendingAction === "completed" && isSubmitting}
                                    />
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleNext}
                        className={`${EVALUATION_BTN.primary} px-6`}
                    >
                        Next
                        <ChevronRightIcon className="h-5 w-5 inline ml-1" />
                    </button>
                )}
            </div>

            {/* Signature Modal Integration */}
            <EvaluationSignatureModal
                isOpen={isSignatureModalOpen}
                onClose={closeSignatureModal}
                onSign={handleSignatureApply}
                disableSign={!signerName}
            >
                <div className="mb-6">
                    <p className={FORM_FIELD.label}>Your Name</p>
                    <div className={FORM_FIELD.readonlyBox}>
                        {signerName || "No name provided"}
                    </div>
                </div>
            </EvaluationSignatureModal>
        </div>
    );
};

EvaluationFormFooter.propTypes = {
    page: PropTypes.number.isRequired,
    setPage: PropTypes.func.isRequired,
    totalPages: PropTypes.number,
    methods: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSignatureModalOpen: PropTypes.bool.isRequired,
    openSignatureModal: PropTypes.func.isRequired,
    closeSignatureModal: PropTypes.func.isRequired,
    handleSignatureApply: PropTypes.func.isRequired,
    signerName: PropTypes.string.isRequired,
    adminSignatureDateField: PropTypes.string,
    reviewedBySignedBy: PropTypes.string,
    reviewedBySignature: PropTypes.string,
    reviewedBySignedAt: PropTypes.string,
    currentStatus: PropTypes.string,
    isReadOnly: PropTypes.bool,
    onNext: PropTypes.func,
};

export default EvaluationFormFooter;
